import React, { useState, useRef } from "react";
import { motion } from "motion/react";
import { toast } from "react-toastify";
import { Settings2, X, Save, FileText, Send, MessageSquare } from "lucide-react";
import SenderPickerList from "./SenderPickerList";
import VariableInsertButtons from "./VariableInsertButtons";
import birthdayRequests from "../../../api/Customer/BirthdayRequests";

// Exactly two settings, per confirmed scope: a default sender ID and a
// default message, used by the backend when it automatically sends birthday
// messages.
//comment
const BirthdaySettingsModal = ({ settings, senderIds, isLoadingSenders, onAddSender, onClose, onSaved }) => {
  const [defaultSenderId, setDefaultSenderId] = useState(settings?.sender_id || "");
  const [defaultMessage, setDefaultMessage] = useState("");
  const [showSavedMessagePreview, setShowSavedMessagePreview] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const textareaRef = useRef(null);

  // "Get Templates" here means "your one saved birthday message" (from
  // settings.message, already fetched by the parent page), not the general
  // SMS-template library — this modal no longer calls that endpoint at all.
  // comment
  const handleUseSavedMessage = () => {
    setDefaultMessage(settings?.message || "");
    setShowSavedMessagePreview(false);
  };

  const handleSave = async () => {
    setIsSubmitting(true);
    try {
      const result = await birthdayRequests.updateBirthdaySettings({
        sender_id: defaultSenderId,
        message: defaultMessage,
      });
      if (result.success) {
        toast.success("Birthday settings saved.");
        onSaved();
        onClose();
      } else {
        toast.error(result.error?.reason || result.error?.message || "Failed to save birthday settings");
      }
    } catch (error) {
      toast.error("An error occurred while saving birthday settings.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm bg-black/40"
      onClick={(event) => event.target === event.currentTarget && onClose()}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.94, y: 24 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.94, y: 24 }}
        transition={{ type: "spring", stiffness: 320, damping: 28 }}
        className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-red-50 to-white">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-xl border border-red-200">
              <Settings2 className="w-4 h-4 text-red-600"/>
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-800">Birthday Settings</h2>
              <p className="text-xs text-gray-500">Defaults used when you start a birthday blast</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors">
            <X className="w-4 h-4"/>
          </button>
        </div>

        <div className="px-6 py-5 space-y-5 max-h-[75vh] overflow-y-auto">
          {/* <p className="text-xs text-gray-400">Both fields below are optional — they're just a starting point for future blasts, not a required setup step.</p> */}

          <div>
            <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">
              <Send className="w-3.5 h-3.5 text-red-500"/> Default Sender ID
            </label>
            <SenderPickerList
              selectedSenderId={defaultSenderId}
              onSelectSenderId={setDefaultSenderId}
              onAddSender={onAddSender}
              senderIds={senderIds}
              isLoading={isLoadingSenders}
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 uppercase tracking-wide">
                <MessageSquare className="w-3.5 h-3.5 text-red-500"/> Default Message
              </label>
              <button
                type="button"
                onClick={() => setShowSavedMessagePreview((currentlyShowing) => !currentlyShowing)}
                className="flex items-center gap-1 text-xs font-semibold text-red-600 hover:text-red-700"
              >
                <FileText className="w-3 h-3"/> {showSavedMessagePreview ? "Hide" : "Get Templates"}
              </button>
            </div>
            {showSavedMessagePreview && (
              <div className="mb-2 p-3 bg-gray-50 rounded-xl border border-gray-100">
                {settings?.message ? (
                  <button
                    type="button"
                    onClick={handleUseSavedMessage}
                    className="flex gap-2 items-start p-3 w-full text-left bg-white rounded-xl border border-gray-200 transition-colors hover:bg-red-50 hover:border-red-200"
                  >
                    <FileText className="flex-shrink-0 mt-0.5 w-4 h-4 text-red-500"/>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-800">Saved Birthday Message</p>
                      <p className="text-xs text-gray-500 whitespace-pre-wrap">{settings.message}</p>
                    </div>
                  </button>
                ) : (
                  <p className="text-xs text-center text-gray-400">No saved message yet — type your own below.</p>
                )}
              </div>
            )}
            <VariableInsertButtons textareaRef={textareaRef} message={defaultMessage} setMessage={setDefaultMessage}/>
            <textarea
              ref={textareaRef}
              value={defaultMessage}
              onChange={(event) => setDefaultMessage(event.target.value)}
              rows={4}
              placeholder="e.g. Happy Birthday! We hope your day is filled with joy."
              className="w-full px-4 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all resize-none"
            />
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between gap-3 bg-gray-50/50">
          <button onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-100 transition-colors">
            Cancel
          </button>
          <motion.button onClick={handleSave} disabled={isSubmitting}
            whileHover={!isSubmitting ? { scale: 1.02 } : {}}
            whileTap={!isSubmitting ? { scale: 0.97 } : {}}
            className={`flex items-center gap-2 px-5 py-2 text-sm font-semibold rounded-xl transition-all shadow-sm ${!isSubmitting ? "bg-gradient-to-r from-red-500 to-red-600 text-white hover:from-red-600 hover:to-red-700 shadow-red-200" : "bg-gray-200 text-gray-400 cursor-not-allowed"}`}>
            {isSubmitting
              ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"/> Saving...</>
              : <><Save className="w-4 h-4"/> Save Settings</>}
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default BirthdaySettingsModal;
